import {
  DataTable,
  IntentBadge,
  StatusBadge,
  formatRelativeTime,
  type DataTableColumn,
} from "@excellent/ui";
import type { EvidenceRunHistoryRowDto } from "@excellent/ui/api/contracts/evidence";

const MUTED = "type-subtext text-muted-foreground";

function statusIntent(status: EvidenceRunHistoryRowDto["status"]) {
  if (status === "fail") return "danger";
  if (status === "pending") return "warning";
  if (status === "pass") return "success";
  return "neutral";
}

function whenLabel(at: string | null): string {
  if (!at) return "generated";
  try {
    return formatRelativeTime(new Date(at.endsWith("Z") ? at : `${at}Z`).toISOString());
  } catch {
    return at;
  }
}

function stepKindLabel(kind: EvidenceRunHistoryRowDto["kind"]): string {
  if (kind === "claim") return "Claim";
  if (kind === "receipt") return "Check";
  if (kind === "verdict") return "Decision";
  if (kind === "artifact") return "Evidence";
  if (kind === "bundle") return "Pack";
  return "Event";
}

function stepLabel(row: EvidenceRunHistoryRowDto): string {
  if (row.label === "claim.assert") return "Workflow claim recorded";
  if (row.label === "evidence.artifact") return "Evidence artifact emitted";
  if (row.label === "offline-verifiable bundle") return "Offline-verifiable pack";
  if (row.label === "reject") return "Rejected by verifier";
  if (row.label === "approve") return "Accepted by verifier";
  return row.label;
}

function stepDetail(detail: string | null | undefined): string | null {
  if (!detail) return null;
  if (detail.startsWith("{") && detail.includes('"id"')) {
    try {
      const parsed = JSON.parse(detail) as unknown;
      if (parsed && typeof parsed === "object") {
        const row = parsed as Record<string, unknown>;
        return [
          typeof row.id === "string" ? row.id : null,
          typeof row.kind === "string" ? row.kind : null,
          typeof row.tier === "number" ? `T${row.tier}` : null,
          typeof row.result === "string" ? row.result : null,
        ]
          .filter(Boolean)
          .join(" · ");
      }
    } catch {
      return detail;
    }
  }
  return detail;
}

const COLUMNS: DataTableColumn<EvidenceRunHistoryRowDto>[] = [
  {
    key: "kind",
    header: "Step",
    render: (r) => {
      const detail = stepDetail(r.detail);
      return (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <IntentBadge intent={statusIntent(r.status)}>{stepKindLabel(r.kind)}</IntentBadge>
            <span className="type-body">{stepLabel(r)}</span>
          </div>
          {detail ? (
            <p className={`${MUTED} line-clamp-2`} title={r.detail ?? detail}>
              {detail}
            </p>
          ) : null}
        </div>
      );
    },
  },
  {
    key: "actor",
    header: "Actor",
    render: (r) => <code className={MUTED}>{r.actor ?? "system"}</code>,
  },
  {
    key: "at",
    header: "When",
    render: (r) => <span className={MUTED}>{whenLabel(r.at)}</span>,
  },
  {
    key: "status",
    header: "Status",
    render: (r) => (
      <StatusBadge
        domain="verification_run"
        status={
          r.status === "fail"
            ? "error"
            : r.status === "pending"
              ? "running"
              : "completed"
        }
        label={r.status}
        withDot
      />
    ),
  },
];

export function VerificationRunHistory({
  rows,
}: {
  rows: EvidenceRunHistoryRowDto[];
}) {
  if (rows.length === 0) {
    return (
      <p className="type-body text-muted-foreground">
        No run history yet. Claims, checks, verifier decisions, evidence
        artifacts, packs, and landing rows appear here as the verification path runs.
      </p>
    );
  }
  return (
    <DataTable
      rows={rows}
      columns={COLUMNS}
      rowKey={(r) => r.key}
      wrapInCard={false}
    />
  );
}
